import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { largeFormatAPI } from '../../utils/api';

const largeFormatTestimonials = [
  {
    id: 1,
    name: 'Mike S.',
    company: 'Verified Customer',
    rating: 5,
    text: 'They did amazing work and a very fast turnaround time. They have saved me many times, I will keep coming back to them.',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&q=80'
  },
  {
    id: 2,
    name: 'Sarah M.',
    company: 'Verified Customer',
    rating: 5,
    text: 'Went in looking for signage, the team was kind and super helpful! Got everything exactly the way I wanted in a good timeline.',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&q=80'
  },
  {
    id: 3,
    name: 'Verified Customer',
    company: 'Santa Ana, CA',
    rating: 5,
    text: 'They were amazing! Very friendly and professional, got back to me right away. Their work is excellent and timely. I highly recommend them.',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&q=80'
  }
];

const LargeFormatHome = () => {
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesData, productsData] = await Promise.all([
          largeFormatAPI.getCategories(),
          largeFormatAPI.getProducts({ featured: true, limit: 8 })
        ]);
        setCategories(categoriesData.categories || []);
        setFeaturedProducts(productsData.products || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const featuredCategories = categories.filter(c => c.featured);

  return (
    <div className="min-h-screen bg-ivory-50">
      {/* Hero Section - Luxury styling */}
      <section className="relative overflow-hidden py-24 gradient-hero">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold-500/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gold-400/5 rounded-full blur-[120px]" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-500/10 border border-gold-500/20 mb-8">
              <span className="text-gold-700 text-sm font-medium font-body">Professional Large Format Printing</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-heading font-semibold mb-6">
              <span className="text-ivory-800">Large Format</span>
              <br />
              <span className="gradient-text-gold">Printing</span>
            </h1>
            <p className="text-xl md:text-2xl text-ivory-500 mb-10 max-w-2xl mx-auto font-body">
              Professional banners, signs, and displays for your business
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6 btn-luxury-primary rounded-lg font-body" 
                asChild
              >
                <Link to="/LargePrint/products" data-testid="browse-large-format-btn">Browse Products</Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="text-lg px-8 py-6 btn-luxury-outline rounded-lg font-body" 
                asChild
              >
                <Link to="/contact">Get Quote</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Categories - Stone gradient */}
      <section className="py-20 gradient-stone">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-heading font-semibold text-ivory-800 mb-4">Shop by Category</h2>
            <p className="text-xl text-ivory-500 font-body">Explore our product range</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {featuredCategories.map((category) => (
              <Link
                key={category.id}
                to={`/LargePrint/products/${category.id}`}
                className="group"
                data-testid={`category-${category.id}`}
              >
                <Card className="bg-ivory-100 border border-ivory-300/50 hover:border-gold-500/50 transition-all duration-300 overflow-hidden card-luxury">
                  <CardContent className="p-8 text-center">
                    <h3 className="text-2xl font-heading font-semibold text-ivory-800 mb-3 group-hover:text-gold-700 transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-ivory-500 mb-6 font-body">{category.description}</p>
                    <div className="flex items-center justify-center text-gold-600 font-semibold font-body">
                      <span>View Products</span>
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products - Pearl gradient */}
      <section className="py-20 gradient-pearl">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-heading font-semibold text-ivory-800 mb-4">Featured Products</h2>
            <p className="text-xl text-ivory-500 font-body">Popular large format printing solutions</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {featuredProducts.map((product) => (
              <Link
                key={product.id}
                to={`/LargePrint/product/${product.id}`}
                className="group"
                data-testid={`product-${product.id}`}
              >
                <Card className="bg-ivory-50 border border-ivory-300/50 hover:border-gold-500/50 transition-all duration-300 overflow-hidden h-full card-luxury">
                  <div className="aspect-square overflow-hidden bg-ivory-200">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-5">
                    <h3 className="font-heading font-semibold text-lg text-ivory-800 mb-2 group-hover:text-gold-700 transition-colors line-clamp-2">
                      {product.name}
                    </h3>
                    <p className="text-ivory-500 text-sm mb-3 line-clamp-2 font-body">{product.description}</p>
                    <div className="text-gold-600 font-bold text-xl font-body">
                      {product.priceType === 'sqft' ? (
                        <span>${product.pricePerSqFt}/sq ft</span>
                      ) : product.sizeOptions ? (
                        <span>From ${product.sizeOptions[0].price}</span>
                      ) : (
                        <span>Contact for pricing</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg" 
              className="btn-luxury-primary rounded-lg font-body" 
              asChild
            >
              <Link to="/LargePrint/products">View All Products</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-ivory-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-heading font-semibold text-ivory-800 mb-4">Customer Reviews</h2>
            <p className="text-xl text-ivory-500 font-body">5.0 star rating from verified customers</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {largeFormatTestimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-ivory-100 border border-ivory-300/50 card-luxury">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-gold-500 text-gold-500" />
                    ))}
                  </div>
                  <p className="text-ivory-600 mb-6 italic font-body">&quot;{testimonial.text}&quot;</p>
                  <div className="flex items-center gap-3">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-gold-500/30"
                    />
                    <div>
                      <div className="font-semibold text-ivory-800 font-body">{testimonial.name}</div>
                      <div className="text-sm text-ivory-500 font-body">{testimonial.company}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LargeFormatHome;
